/**
 * Hook for fetching workflow run diagnostics (console logs, network requests).
 *
 * Supports lazy loading - diagnostics are only fetched when `enabled` is true,
 * which typically happens when the user opens the fullscreen video player and
 * switches to the Console or Network tab.
 *
 * @module hooks/useWorkflowRunDiagnostics
 */

import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../config.js';
import { toApiError } from '../lib/api-error';
import type { TimestampedConsoleLog, TimestampedNetworkRequest } from '@chatsdet/types';

interface WorkflowRunDiagnosticsData {
  consoleLogs: TimestampedConsoleLog[];
  networkRequests: TimestampedNetworkRequest[];
  videoStartedAt: string | null;
}

interface DiagnosticsResponse {
  ok: boolean;
  data?: WorkflowRunDiagnosticsData;
  error?: string;
}

/**
 * Fetch diagnostics for a workflow run.
 *
 * @param workflowId - The workflow ID
 * @param runId - The run ID
 * @param enabled - Whether to fetch diagnostics (for lazy loading)
 */
export function useWorkflowRunDiagnostics(
  workflowId: string | undefined,
  runId: string | undefined,
  enabled: boolean = false
) {
  return useQuery<WorkflowRunDiagnosticsData>({
    queryKey: ['workflow-run-diagnostics', workflowId, runId],
    queryFn: async () => {
      if (!workflowId || !runId) {
        return { consoleLogs: [], networkRequests: [], videoStartedAt: null };
      }

      const res = await fetch(`${API_URL}/workflows/${workflowId}/runs/${runId}/diagnostics`, {
        credentials: 'include',
      });

      if (!res.ok) {
        throw await toApiError(res, 'Failed to load diagnostics');
      }

      const json: DiagnosticsResponse = await res.json();

      if (!json.ok || !json.data) {
        return { consoleLogs: [], networkRequests: [], videoStartedAt: null };
      }

      return json.data;
    },
    enabled: enabled && Boolean(workflowId) && Boolean(runId),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
}
